/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The geofence endpoints. The reads are live; the writes are placeholders that answer 501 until
 * the backend can store geofences (api-insights/docs/decisions/geofence-management.md), so
 * `writeFailure` turns that, and the stale-version 409 the real writes will send, into
 * something the screens can say.
 */

import type { AxiosError } from 'axios'

import { buildQuery } from '@/insights/common/queryUtils'
import type { ApiService } from '@/types/dashboard-types'

import { boundaryResultFromResponse, type GeofencesByBoundariesApiItem } from './geofenceBoundaryParser'
import type {
  GeofenceCategory,
  GeofenceCategoryWriteBody,
  GeofenceBounds,
  GeofenceDetail,
  GeofenceWriteBody,
  GeofencesByBoundariesResult,
  ManagedGeofence,
} from './types'

const ORIGIN = 'geofences'

export const fetchManagedGeofences = (api: ApiService, companyId: number): Promise<ManagedGeofence[]> =>
  api
    .simpleGet<{ data: ManagedGeofence[] }>({ path: `/fresh/geofences/manage?companyId=${companyId}`, origin: ORIGIN })
    .then((r) => r.data)

export const fetchGeofencesByBoundaries = (
  api: ApiService,
  companyId: number,
  viewport: GeofenceBounds
): Promise<GeofencesByBoundariesResult> => {
  const params = buildQuery({
    companyId,
    east: viewport.east,
    north: viewport.north,
    south: viewport.south,
    west: viewport.west,
  })

  return api
    .get<GeofencesByBoundariesApiItem[]>({
      path: '/fresh/geofences/by-boundaries?' + params,
      origin: 'LocationMap',
    })
    .then((response) => {
      if (response.error) throw new Error(response.message ?? 'Could not load geofences in the map region.')
      if (!Array.isArray(response.data)) throw new Error('Could not load geofences in the map region.')
      return boundaryResultFromResponse(response.data, companyId)
    })
}

export const fetchGeofence = (api: ApiService, id: number): Promise<GeofenceDetail> =>
  api.simpleGet<{ data: GeofenceDetail }>({ path: `/fresh/geofences/${id}`, origin: ORIGIN }).then((r) => r.data)

export const fetchGeofenceCategories = (api: ApiService, companyId: number): Promise<GeofenceCategory[]> =>
  api
    .simpleGet<{ data: GeofenceCategory[] }>({
      path: `/fresh/geofence-categories?companyIds=${companyId}&skip=0&take=10000`,
      origin: ORIGIN,
    })
    .then((r) => r.data.map(({ id, name, companyId: owner, typeId }) => ({ id, name, companyId: owner, typeId })))

export const createGeofence = (api: ApiService, companyId: number, body: GeofenceWriteBody): Promise<GeofenceDetail> =>
  api
    .simplePost<{ data: GeofenceDetail }, GeofenceWriteBody & { companyId: number }>({
      path: '/fresh/geofences',
      body: { ...body, companyId },
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const updateGeofence = (
  api: ApiService,
  id: number,
  version: number,
  body: GeofenceWriteBody
): Promise<GeofenceDetail> =>
  api
    .simplePut<{ data: GeofenceDetail }, GeofenceWriteBody & { version: number }>({
      path: `/fresh/geofences/${id}`,
      body: { ...body, version },
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const deleteGeofence = (api: ApiService, id: number, version: number): Promise<void> =>
  api.simpleRemove({ path: `/fresh/geofences/${id}?version=${version}`, origin: ORIGIN }).then(() => undefined)

export const createGeofenceCategory = (
  api: ApiService,
  companyId: number,
  body: GeofenceCategoryWriteBody
): Promise<GeofenceCategory> =>
  api
    .simplePost<{ data: GeofenceCategory }, GeofenceCategoryWriteBody & { companyId: number }>({
      path: '/fresh/geofence-categories',
      body: { ...body, companyId },
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const updateGeofenceCategory = (
  api: ApiService,
  id: number,
  body: GeofenceCategoryWriteBody
): Promise<GeofenceCategory> =>
  api
    .simplePut<{ data: GeofenceCategory }, GeofenceCategoryWriteBody>({
      path: `/fresh/geofence-categories/${id}`,
      body,
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const deleteGeofenceCategory = (api: ApiService, id: number): Promise<void> =>
  api.simpleRemove({ path: `/fresh/geofence-categories/${id}`, origin: ORIGIN }).then(() => undefined)

export interface WriteFailure {
  /** unavailable: the write is still a placeholder (501). conflict: someone saved first (409). */
  kind: 'unavailable' | 'conflict' | 'failed'
  message: string
}

export const writeFailure = (error: unknown): WriteFailure => {
  const response = (error as AxiosError<{ message?: string }>).response
  const message = response?.data?.message ?? (error instanceof Error ? error.message : 'Something went wrong')
  if (response?.status === 501) return { kind: 'unavailable', message }
  if (response?.status === 409) return { kind: 'conflict', message }
  return { kind: 'failed', message }
}
